import { Stack } from '@cast/design-system';
import { ComputeResourceType } from '@cast/shared-types';

import {
  computeResourceUnitsMap,
  computeResourceWordMap,
} from 'constants/maps';

import { ChartWidget } from '../ChartWidget';
import { ChartWithPrediction } from './ChartWithPrediction';
import { useWorkloadSelectedContainer } from '../_hooks/useWorkloadSelectedContainer';
import { useRightsizingTimeRangeSelection } from '../../../_hooks/useRightsizingTimeRangeSelection';
import {
  predictionStrokeDasharrayMap,
  percentileToColor,
  percentileToTitle,
} from '../constants';
import {
  CurrentRequestMetricBox,
  RecommendedMetricBox,
  RecommendedRequestMetricBoxes,
} from '../RecommendedRequestMetricBoxes';
import { UsageMetricsBoxes } from '../UsageMetricsBoxes';
import { useCpuPredictionChartData } from './useCpuPredictionChartData';

export const CpuPredictionChartSection = () => {
  const { container } = useWorkloadSelectedContainer();
  const { timeZone } = useRightsizingTimeRangeSelection();
  const {
    workload,
    isFetching,
    error,
    refetch,
    chartData,
    toggleMetric,
    isMetricDisabled,
  } = useCpuPredictionChartData({ container, timeZone });

  const hasPredictions = workload?.predictionInsights?.isEligible;

  return (
    <ChartWidget
      testId="cpu-prediction-chart"
      title="CPU predictive autoscaling"
      chartEl={
        <ChartWithPrediction
          timeZone={timeZone}
          unit="CPU"
          analyzeDescription="VPA cpu metric with prediction"
          data={hasPredictions ? chartData?.chartData ?? [] : []}
          dataKeys={chartData?.chartDataKeys ?? []}
          colorMap={percentileToColor}
          tooltipTitleMap={percentileToTitle}
          strokeDasharrayMap={
            chartData?.isReadOnly ? predictionStrokeDasharrayMap : {}
          }
          isLoading={isFetching || !workload || !chartData}
          hasError={!!error && !isFetching}
          refetch={refetch}
          isMetricDisabled={isMetricDisabled}
        />
      }
      primaryMetricsEl={
        chartData && workload ? (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <RecommendedRequestMetricBoxes
              reverse
              recommendationEl={
                <RecommendedMetricBox
                  type={ComputeResourceType.CPU}
                  title={`${computeResourceWordMap[ComputeResourceType.CPU]} ${
                    percentileToTitle.rec
                  }`}
                  acronym={computeResourceUnitsMap[ComputeResourceType.CPU]}
                  markerColor={percentileToColor.rec}
                  recommendation={workload.recommendation}
                  from={chartData.predictionsAggregated?.avgRec}
                  to={chartData.recommended}
                  isReadOnly={chartData.isReadOnly}
                  resourceConfig={workload.workloadConfigV2.vpaConfig.cpu}
                  isDisabled={isMetricDisabled('rec')}
                  onClick={() => toggleMetric('rec')}
                />
              }
              currentEl={
                <CurrentRequestMetricBox
                  title={percentileToTitle.recPredicted}
                  acronym={computeResourceUnitsMap[ComputeResourceType.CPU]}
                  markerColor={percentileToColor.recPredicted}
                  from={chartData.predictionsAggregated?.avgRec}
                  to={chartData.recommended}
                  isDisabled={isMetricDisabled('recPredicted')}
                  onClick={() => toggleMetric('recPredicted')}
                />
              }
            />
          </Stack>
        ) : null
      }
      secondaryMetricsEl={
        chartData ? (
          <UsageMetricsBoxes
            metricsBoxes={['avg', 'avgPredicted']}
            unit="CPU"
            summary={chartData.usageSummaryWithPredictions}
            titleMap={percentileToTitle}
            colorMap={percentileToColor}
            toggleMetricsBox={toggleMetric}
            isMetricDisabled={isMetricDisabled}
          />
        ) : undefined
      }
    />
  );
};
